import { useState } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import { useQueryClient } from '@tanstack/react-query';
import { http } from '../../features/jobs/api/http';
import TokenCorrectionPreview from './TokenCorrectionPreview';

// European keyboards type 3.605,5 — accept comma as the decimal separator and
// dots as thousands, the same way the rest of the app's numeric inputs do.
const toNum = v => {
  if (v === null || v === undefined) return NaN;
  const s = String(v).trim().replace(/\s/g, '');
  if (!s) return NaN;
  const normalised = s.includes(',') ? s.replace(/\./g, '').replace(',', '.') : s;
  return Number(normalised);
};

const fmt = (v, d = 0) =>
  Number(v || 0).toLocaleString('ro-RO', { minimumFractionDigits: d, maximumFractionDigits: d });

/**
 * Correct the recorded length of a finished drum.
 *
 * The length is not stored once — it is the drum's, the task's, the parent
 * drum's consumption, the job material summary the dashboards read, and the
 * per-drum rows Operator Performance aggregates. So the correction is never
 * applied blind: a dry run comes back with every derived field it would change,
 * and only then is there something to confirm.
 */
const CorrectTokenLengthModal = ({ show, onHide, jobId, tokenId, token }) => {
  const queryClient = useQueryClient();
  const [value, setValue] = useState('');
  const [reason, setReason] = useState('');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null);

  const current = Number(token?.producedLength ?? 0);
  const parsed = toNum(value);
  const valid = Number.isFinite(parsed) && parsed >= 0;

  const reset = () => {
    setValue('');
    setReason('');
    setPreview(null);
    setError(null);
    setDone(null);
  };

  const close = () => {
    reset();
    onHide();
  };

  const run = async dryRun => {
    setBusy(true);
    setError(null);
    try {
      const { data } = await http.post(
        `/api/redis/jobs/${jobId}/tokens/${tokenId}/correct-length`,
        { producedLength: parsed, reason, dryRun }
      );
      if (dryRun) {
        setPreview(data);
      } else {
        setDone(data);
        setPreview(null);
        // Everything downstream reads this job or these drums.
        queryClient.invalidateQueries({ queryKey: ['jobs'] });
        queryClient.invalidateQueries({ queryKey: ['operator-dashboard'] });
        queryClient.invalidateQueries({ queryKey: ['production-dashboard'] });
      }
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Request failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal show={show} onHide={close} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          Correct length — <span style={{ fontFamily: 'monospace' }}>{tokenId}</span>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {done ? (
          <>
            <Alert variant="success">
              Corrected from <strong>{fmt(done.from)} m</strong> to <strong>{fmt(done.to)} m</strong>.{' '}
              {done.changes.length} derived field(s) and {done.metricRecords.length} execution
              record(s) updated.
            </Alert>
            {!!done.warnings?.length && (
              <Alert variant="warning" className="fs-9 mb-0">
                {done.warnings.map((w, i) => (
                  <div key={i}>{w}</div>
                ))}
              </Alert>
            )}
          </>
        ) : (
          <>
            <div className="mb-3 text-700 fs-9">
              Recorded now: <strong>{fmt(current)} m</strong>
              {token?.plannedLength ? <> · planned {fmt(token.plannedLength)} m</> : null}
            </div>

            <Form.Group className="mb-3">
              <Form.Label>Correct length (m)</Form.Label>
              <Form.Control
                type="text"
                inputMode="decimal"
                autoFocus
                value={value}
                placeholder={String(current)}
                onChange={e => {
                  setValue(e.target.value);
                  setPreview(null);
                }}
              />
              {value && !valid && (
                <Form.Text className="text-danger">Enter a number of metres, 0 or more.</Form.Text>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                Reason <span className="text-700 fw-normal">(kept on the record)</span>
              </Form.Label>
              <Form.Control
                type="text"
                value={reason}
                placeholder="e.g. meter glitch — counter jumped mid-drum"
                onChange={e => setReason(e.target.value)}
              />
            </Form.Group>

            {error && <Alert variant="danger">{error}</Alert>}

            <TokenCorrectionPreview preview={preview} />
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        {done ? (
          <Button variant="primary" onClick={close}>
            Close
          </Button>
        ) : (
          <>
            <Button variant="link" onClick={close} disabled={busy}>
              Cancel
            </Button>
            <Button variant="secondary" onClick={() => run(true)} disabled={!valid || busy}>
              {busy && !preview ? <Spinner size="sm" animation="border" className="me-2" /> : null}
              Preview
            </Button>
            <Button
              variant="primary"
              onClick={() => run(false)}
              disabled={!valid || busy || !preview || preview.noop}
              title={!preview ? 'Preview first' : undefined}
            >
              {busy && preview ? <Spinner size="sm" animation="border" className="me-2" /> : null}
              Apply correction
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default CorrectTokenLengthModal;
